import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as TickIcon } from '../../../../assets/icons/tick.svg';
import '../../../../Components/Common/typography.css';
import './SelectDropdown.css';

export interface SelectOption {
  label: string;
  value: string | number;
}

const LINE_STYLE_OPTIONS: SelectOption[] = [
  { label: 'Solid', value: 's' },
  { label: 'Dash', value: 'd' },
];

const THICKNESS_OPTIONS: SelectOption[] = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((t) => ({
  label: String(t),
  value: t,
}));

type SelectDropdownType = 'linestyle' | 'thickstyle';

interface SelectDropdownProps {
  options?: SelectOption[];
  type?: SelectDropdownType;
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  className?: string;
}

const TYPED_OPTIONS: Record<SelectDropdownType, SelectOption[]> = {
  linestyle: LINE_STYLE_OPTIONS,
  thickstyle: THICKNESS_OPTIONS,
};

export type { SelectDropdownType };

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  type,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
}) => {
  const resolvedOptions = type ? TYPED_OPTIONS[type] : (options ?? []);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      minWidth: rect.width,
      zIndex: 9999,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) return;
      setIsOpen(false);
    };


    const handleScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const selectedLabel = resolvedOptions.find((o) => o.value === value)?.label
    ?? (type ? resolvedOptions[0]?.label : placeholder);

  return (
    <div className={`select-dropdown ${className}`}>
      <button
        ref={triggerRef}
        className="select-dropdown-trigger body-m-regular"
        onClick={() => { if (!isOpen) updatePosition(); setIsOpen((v) => !v); }}
        type="button"
      >
        <span className="select-dropdown-label">{selectedLabel}</span>
        <ArrowDownIcon className="select-dropdown-arrow" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="select-dropdown-menu"
          style={menuStyle}
          data-outside-ignore="true"
        >
          {resolvedOptions.map((option) => (
            <div
              key={option.value}
              className={`select-dropdown-item body-m-regular${value === option.value ? ' select-dropdown-item-active' : ''}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              <span>{option.label}</span>
              {value === option.value && <TickIcon className="select-dropdown-tick" />}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SelectDropdown;
